/**
 * @file CountersModeGroup.tsx
 * @description Группа «Когда включать» панели «Счётчики и cookie»
 * (phase19.16, партия Р3г, v0.19.62; SB133):
 *  - «Только после согласия» — умолчание и рекомендация;
 *  - «Уведомление» — через окно о риске с галочкой «отвечаю сам»
 *    (`NoticeRiskDialog`, тексты текущей редакции платформы); запрос несёт
 *    `noticeAck` с номером показанной редакции;
 *  - платформа запретила «Уведомление» (`noticeAllowed: false`) — вариант
 *    неактивен с подписью; если он уже выбран в черновике — полоса с
 *    переключением на «после согласия» (сохранить черновик с ним нельзя).
 * Радиокнопки — нативные `input type="radio"` в строках от 48 px.
 */

import { SITE_COUNTER_MODES, SITE_COUNTER_MODE_LABELS, enabledCounterServices, type SiteCounterMode } from "@chaiberi/shared";
import { useId, useState } from "react";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { useCounters } from "./counters-context";
import { NoticeRiskDialog } from "./NoticeRiskDialog";
import { noticeRiskOf } from "../../../lib/site-view/consent-platform";
import { MODE_HINTS, NOTICE_FORBIDDEN_DRAFT_TEXT, NOTICE_FORBIDDEN_TEXT, modeSummary } from "../../../lib/site-view/counters-form";
import { cn } from "@/lib/utils";

export function CountersModeGroup() {
  const { counters, platform, noticeAllowed, update } = useCounters();
  const name = useId();
  const [asking, setAsking] = useState(false);
  const forbiddenDraft = !noticeAllowed && counters.mode === "notice";
  const noCounters = enabledCounterServices(counters).length === 0;

  function pick(mode: SiteCounterMode) {
    if (mode === counters.mode) return;
    // «Уведомление» — только через окно о риске с галочкой.
    if (mode === "notice") setAsking(true);
    else update({ mode });
  }

  return (
    <SettingsGroup
      id="mode"
      title="Когда включать"
      tocLabel="Когда"
      hint="Когда счётчики начинают работать у посетителя."
      summary={modeSummary(counters, noticeAllowed)}
      status={counters.mode === "notice" ? "warning" : undefined}
    >
      <div role="radiogroup" aria-label="Когда включать счётчики" className="flex min-w-0 flex-col gap-2">
        {SITE_COUNTER_MODES.map((mode) => {
          const locked = mode === "notice" && !noticeAllowed;
          return (
            <label
              key={mode}
              className={cn(
                "flex min-h-12 min-w-0 cursor-pointer items-start gap-3 rounded-xl px-3 py-3",
                counters.mode === mode ? "bg-primary/10" : "bg-surface-low",
                locked && "cursor-not-allowed opacity-60",
              )}
            >
              <input
                type="radio"
                name={name}
                className="mt-1 size-4 shrink-0 accent-[var(--primary)]"
                checked={counters.mode === mode}
                disabled={locked}
                onChange={() => pick(mode)}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="type-body font-medium text-on-surface">
                  {SITE_COUNTER_MODE_LABELS[mode]}
                  {mode === "consent" ? " (рекомендуем)" : null}
                </span>
                <span className="type-caption text-on-surface-variant">{locked ? NOTICE_FORBIDDEN_TEXT : MODE_HINTS[mode]}</span>
              </span>
            </label>
          );
        })}
      </div>
      {forbiddenDraft ? (
        <NoticeStrip tone="warning" action={<Button variant="outline" onClick={() => update({ mode: "consent" })}>Только после согласия</Button>}>
          {NOTICE_FORBIDDEN_DRAFT_TEXT}
        </NoticeStrip>
      ) : null}
      {noCounters ? <FieldHint className="mt-0">Пока счётчики не подключены, режим не действует.</FieldHint> : null}
      <NoticeRiskDialog
        open={asking}
        texts={noticeRiskOf(platform).texts}
        onCancel={() => setAsking(false)}
        onConfirm={() => {
          setAsking(false);
          update({ mode: "notice" }, { noticeAck: true });
        }}
      />
    </SettingsGroup>
  );
}
